import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { getLinkData, BranchLink, BranchIOLinkData } from "web/script/utils/branch-io";
import { useClickTracker } from "web/react/hooks/use-click-tracker/use-click-tracker";
import { gettext } from "web/script/modules/django-i18n";
import analytics from "web/script/analytics/analytics";
import styles from "./app-download-button.css";

type AppButtonType = "white" | "whiteSmall" | "black" | "teaser";
type AppDownloadLink = "footer" | "appPage" | "appTeaser";

const STYLE_TYPE_CLASSES: { readonly [bt in AppButtonType]: string } = {
    white: styles.appBadgeWhite,
    whiteSmall: styles.appBadgeWhiteSmall,
    black: styles.appBadgeBlack,
    teaser: styles.appTeaser,
};

const APP_DOWNLOAD_LINKS: { readonly [lnk in AppDownloadLink]: BranchLink } = {
    footer: BranchLink.Footer,
    appPage: BranchLink.AppPage,
    appTeaser: BranchLink.AppTeaser,
};

interface AppDownloadButtonProps {
    appButtonType: AppButtonType;
    appDownloadLink: AppDownloadLink;
    eventLabel: string;
    eventCategory: string;
    eventAction: string;
    titleId?: string;
}

/**
 * A custom hook for keeping a Branch.io download URL up to date with analytics data
 * @param {string} appDownloadLinkKey The APP_DOWNLOAD_LINKS key to the Branch.io download URL to track
 * @returns {string} The augmented lead URL
 */
export function useBranchHref(appDownloadLinkKey: AppDownloadLink): string {
    const branchLink = APP_DOWNLOAD_LINKS[appDownloadLinkKey];
    const [branchHref, setBranchHref] = useState(branchLink.toString());
    const branchReferralLink = new URL(APP_DOWNLOAD_LINKS[appDownloadLinkKey]);

    useEffect(() => {
        function updateHref(): void {
            let branchIOLinkData: BranchIOLinkData = getLinkData();
            for (const branchDataProperty in branchIOLinkData) {
                branchReferralLink.searchParams.set(
                    branchDataProperty,
                    branchIOLinkData[branchDataProperty]
                );
            }
            setBranchHref(branchReferralLink.toString());
        }
        updateHref();
        analytics.on("pageViewIdChanged", updateHref);
        return function cleanup(): void {
            analytics.off("pageViewIdChanged", updateHref);
        };
    }, [branchReferralLink, appDownloadLinkKey]);

    return branchHref;
}

export function AppDownloadButton({
    appButtonType,
    appDownloadLink,
    eventCategory,
    eventAction,
    eventLabel,
    titleId,
}: AppDownloadButtonProps): JSX.Element {
    const typeClass = STYLE_TYPE_CLASSES[appButtonType];
    let title = "";
    if (titleId) {
        title = gettext(titleId);
    }
    let branchLink = useBranchHref(appDownloadLink);
    let trackedClick = useClickTracker(eventCategory, eventAction, eventLabel);
    return (
        <a
            className={classNames(styles.appDownloadImage, typeClass)}
            href={branchLink}
            onClick={trackedClick}
            title={title}
        />
    );
}

export default AppDownloadButton;
